// packages
import { Typography } from '@mui/material';
import React from 'react';
import StatusFlag from '../../../../mui/StatusFlag';
import styles from './InvoiceViewStatus.module.scss';
import { useDeviceDetection } from '../../../../hooks/useDeviceDetection';
import { Status } from '../../../Status';
import { InvoiceStatus, InvoiceViewStatusInvoiceFragment } from '#types';
import { gql, DocumentNode } from '@apollo/client';
import rpcShared from '@rockpapercoin/rpc-shared';

const invoiceViewStatusInvoiceFragment = gql`
	fragment invoiceViewStatusInvoice on Invoice {
		id
		status
		isPastDue
		contact {
			customer {
				id
				clientUser {
					id
					plannerOrgCanPayInvoices
					assignedPlanner {
						id
						organization {
							id
							users {
								id
								user {
									id
								}
							}
						}
					}
				}
			}
		}
		paymentInstallments {
			id
			dueDate
			status
		}
	}
`;

export type InvoiceViewStatusProps = {
	invoice?: InvoiceViewStatusInvoiceFragment;
	requestingUserBaseId?: string;
	requestingUserIsGuest?: boolean;
};

type InvoiceViewStatusPropsWithFragment = React.FC<InvoiceViewStatusProps> & {
	fragments: {
		invoiceViewStatusInvoiceFragment: DocumentNode;
	};
};

export const InvoiceViewStatus: InvoiceViewStatusPropsWithFragment = ( {
	invoice,
	requestingUserBaseId,
	requestingUserIsGuest,
} ) => {
	const { isMobile } = useDeviceDetection();

	const viewingUserIsCustomer = requestingUserBaseId
		? rpcShared.invoices.isCustomer( invoice, requestingUserBaseId )
		: false;
	const orgUser =
		invoice?.contact?.customer?.clientUser?.assignedPlanner?.organization?.users?.find(
			( user ) => user.user?.id === requestingUserBaseId
		);
	const viewingUserIsAPPA =
		requestingUserBaseId && invoice
			? rpcShared.invoices.isAssignedPlannerOrAdminOfCustomersPlannerOrg(
				invoice,
				orgUser
			  )
			: false;
	const viewingUserIsAPPAWithPaymentPerms =
		viewingUserIsAPPA &&
		!!invoice?.contact?.customer?.clientUser?.plannerOrgCanPayInvoices;

	const viewingUserIsCustomerOrAPPAWithPaymentPerms =
		viewingUserIsCustomer || viewingUserIsAPPAWithPaymentPerms;

	const status =
		typeof invoice?.paymentInstallments !== 'undefined'
			? rpcShared.invoices.getUserFriendlyStatus(
				invoice,
				viewingUserIsCustomerOrAPPAWithPaymentPerms
			  )
			: InvoiceStatus.Draft;

	return typeof status === 'string' ? (
		<>
			{ isMobile ? (
				<div className={ styles.mobileFlag }>
					<Typography className={ styles.statusText }>Status</Typography>
					<div className={ styles.statusWidget }>
						<Status status={ status } />
					</div>
				</div>
			) : (
				<StatusFlag
					status={ status }
					className={
						requestingUserIsGuest
							? styles.desktopFlagForGuest
							: styles.desktopFlag
					}
				/>
			) }
		</>
	) : null;
};

InvoiceViewStatus.fragments = {
	invoiceViewStatusInvoiceFragment,
};
